import type { AbstractAgent, RunAgentInput } from "@ag-ui/client";
import { Middleware } from "@ag-ui/client";
import type { BaseEvent, CustomEvent } from "@ag-ui/core";
import { EventType } from "@ag-ui/core";
import type { Observable } from "rxjs";
import { filter } from "rxjs";

/**
 * Extracts `conversation_name` CustomEvents out-of-band before they reach the
 * AG-UI verifier, which would otherwise reject any event arriving outside the
 * RUN_STARTED..RUN_FINISHED envelope. The display name is conversation
 * metadata, not a turn-lifecycle event, so the callback is invoked directly
 * and the event is dropped from the stream regardless of its position.
 */
export class ConversationNameMiddleware extends Middleware {
  constructor(private readonly onName: (conversationId: string, displayName: string) => void) {
    super();
  }

  run(input: RunAgentInput, next: AbstractAgent): Observable<BaseEvent> {
    return this.runNext(input, next).pipe(
      filter((event) => {
        if (event.type !== EventType.CUSTOM) {
          return true;
        }
        const custom = event as CustomEvent;
        if (custom.name !== "conversation_name") {
          return true;
        }
        const value = custom.value as
          | { display_name?: unknown; conversation_id?: unknown }
          | undefined;
        if (typeof value?.display_name === "string" && typeof value?.conversation_id === "string") {
          this.onName(value.conversation_id, value.display_name);
        }
        return false;
      })
    );
  }
}
